import { dataService } from './dataService.js';
import { appStore, actions } from '../store/appStore.js';
import { ErrorCode } from '../constants/errorCodes.js';
import { ApiError } from './api.js';
import { audioNotificationService } from './audioNotificationService.js';

export class BarcodeScannerService {
  private hiddenInput: HTMLInputElement | null = null;
  private isFormInputActive = false;
  private autoCloseTimeoutId: number | null = null;

  initialize() {
    this.createHiddenInput();
    this.setupEventListeners();
    this.focusHiddenInput();

    // Initialize audio on first user interaction
    document.addEventListener('click', () => {
      audioNotificationService.initializeOnUserInteraction();
    }, { once: true });
  }

  private createHiddenInput() {
    // Remove existing hidden input if present
    const existingInput = document.getElementById('barcode-scanner-input');
    if (existingInput) {
      existingInput.remove();
    }

    // Create new hidden input
    this.hiddenInput = document.createElement('input');
    this.hiddenInput.id = 'barcode-scanner-input';
    this.hiddenInput.type = 'text';
    this.hiddenInput.style.position = 'fixed';
    this.hiddenInput.style.left = '-9999px';
    this.hiddenInput.style.top = '-9999px';
    this.hiddenInput.style.opacity = '0';
    this.hiddenInput.style.pointerEvents = 'none';
    this.hiddenInput.style.zIndex = '9999'; // Higher than drawer z-index
    this.hiddenInput.setAttribute('autocomplete', 'off');
    this.hiddenInput.setAttribute('autocapitalize', 'off');
    this.hiddenInput.setAttribute('autocorrect', 'off');
    this.hiddenInput.setAttribute('tabindex', '0'); // Make it focusable

    document.body.appendChild(this.hiddenInput);

    // Handle barcode scan input - only submit on Enter key
    this.hiddenInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        const value = this.hiddenInput!.value.trim();
        if (value) {
          this.handleBarcodeInput({ target: { value } } as any);
          this.hiddenInput!.value = '';
        }
      }

      if (event.key === 'Escape') {
        this.hiddenInput!.value = '';
      }
    });
  }

  private setupEventListeners() {
    // Track when form inputs become active
    document.addEventListener('focusin', (event) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA') {
        if (target !== this.hiddenInput) {
          this.isFormInputActive = true;
        }
      }
    });

    // Re-focus hidden input when form inputs lose focus
    document.addEventListener('focusout', (event) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA') {
        if (target !== this.hiddenInput) {
          this.isFormInputActive = false;
          this.focusHiddenInput();
        }
      }
    });

    // Handle clicks to maintain focus on hidden input (use capture to survive stopPropagation in drawer)
    document.addEventListener('click', () => {
      // After the click settles, if a real text-entry control isn't focused, refocus the hidden input
      requestAnimationFrame(() => {
        const active = document.activeElement as HTMLElement | null;
        const isTextEntryActive = !!active && (
          active.matches('input, textarea, select, [contenteditable="true"]')
        );
        if (!isTextEntryActive) {
          this.focusHiddenInput();
        }
      });
    }, true);
  }

  private async handleBarcodeInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const scannedCode = input.value.trim();

    if (!scannedCode) return;

    // Clear the input and keep focus
    this.clearAndRefocus();

    await this.handleBagQRScan(scannedCode);
  }

  private async handleBagQRScan(bagCode: string) {
    const state = appStore.getState();
    const linkedPackLineId = state.linkedPackLine?.id;
    const { actions } = await import('../store/appStore.js');

    if (!linkedPackLineId) {
      actions.addAlert('No line found. Please link this device to an active line before resuming bag scanning.', 'error');
      audioNotificationService.playErrorSound();
      console.warn('No active pack line session. Please link this device to an active line first.');
      return;
    }

    try {
      // Use the full scanned code as delivery ID (no prefix assumption)
      const deliveryId = bagCode;

      // Call API to record delivery scan
      await dataService.scanDeliveryQR(linkedPackLineId, bagCode);

      console.log(`Bag scanned: ${deliveryId} for pack line: ${linkedPackLineId}`);
    } catch (error) {
      console.error('Failed to scan bag QR:', error);
      // Handle specific API errors using error codes
      if (error instanceof ApiError && error.code) {
        audioNotificationService.playErrorSound();
        switch (error.code) {
          case ErrorCode.PACK_SESSION_NOT_STARTED || ErrorCode.PACK_LINE_ID_NOT_FOUND:
            actions.addAlert('No line found. Please scan an active line before resuming bag scanning.', 'error');
            break;
          case ErrorCode.PACK_LINE_ON_BREAK:
            actions.addAlert('Line is on break. Please end the pack line break before resuming bag scanning.', 'error');
            break;
          case ErrorCode.PACK_LINE_HAS_ENDED:
            actions.addAlert('Line has ended. Please scan an active line before resuming bag scanning.', 'error');
            barcodeScanner.handlePackLineHasEnded();
            break;
          case ErrorCode.DELIVERY_QR_CODE_NOT_FOUND:
            actions.addAlert('Bag QR code not found. Please check the code and try again.', 'error');
            break;
          case ErrorCode.DELIVERY_CYCLE_MISMATCH:
            actions.addAlert('Bag does not belong to this pack line\'s cycle.', 'error');
            break;
          case ErrorCode.DELIVERY_FACILITY_MISMATCH:
            actions.addAlert('Bag does not belong to this pack line\'s facility.', 'error');
            break;
          default:
            actions.addAlert('Failed to scan bag. Please try again.', 'error');
        }
      } else {
        actions.addAlert('Failed to scan bag. Please try again.', 'error');
        audioNotificationService.playErrorSound();
      }
    }
  }

  private clearAndRefocus() {
    if (this.hiddenInput) {
      this.hiddenInput.value = '';
      this.focusHiddenInput();
    }
  }

  private focusHiddenInput() {
    if (!this.hiddenInput) return;

    // Check if another form input is currently focused
    const activeElement = document.activeElement as HTMLElement;
    const isFormElementFocused = activeElement &&
      (activeElement.tagName === 'INPUT' ||
       activeElement.tagName === 'SELECT' ||
       activeElement.tagName === 'TEXTAREA') &&
      activeElement !== this.hiddenInput;

    if (isFormElementFocused) return;

    this.hiddenInput.focus({ preventScroll: true });
  }

  async handlePackLineHasEnded() {
    console.log("Linked to inactive pack line, unlinking")
    await barcodeScanner.clearLinkedPackLine();
  }

  async clearLinkedPackLine() {
    const state = appStore.getState();
    if (state.linkedPackLine) {
      state.linkedPackLine.isLinked = false;
      await actions.updatePackLine(state.linkedPackLine);
    }
    actions.setLinkedPackLine(null);
  }

  getLinkedPackLineId(): number | null {
    const state = appStore.getState();
    return state.linkedPackLine?.id || null;
  }

  // Cancel any pending auto-close operation (when user manually interacts with drawer)
  cancelAutoClose() {
    if (this.autoCloseTimeoutId !== null) {
      window.clearTimeout(this.autoCloseTimeoutId);
      this.autoCloseTimeoutId = null;
    }
  }

  // Method to manually focus the hidden input (useful for debugging)
  focus() {
    this.focusHiddenInput();
  }

  async destroy() {
    if (this.hiddenInput) {
      this.hiddenInput.remove();
      this.hiddenInput = null;
    }
    await this.clearLinkedPackLine();
  }
}

export const barcodeScanner = new BarcodeScannerService();
